/* eslint-disable no-lone-blocks */

'use client';

import { useCallback, useEffect, useRef, type RefObject } from 'react';
import { debounce } from '@funkey/shared';
import { cva } from 'class-variance-authority';
import { motion } from 'motion/react';
import { Icon } from '~/components/primitives';
import { useStateObject } from '~/utils/react';
import { cn } from '~/utils';

const CENTER_CLASS = 'md:justify-center';
const OUTER_PADDING_CLASS = 'pr-4';

const Carousel: React.FC<
    {
        buttons?: boolean;
        /** @default false */
        disableMarginLeft?: boolean;

        /** @default 'MD' */
        spacing?: CarouselContainer.Props['spacing'];

        /** Callback triggered when user approaches the end of available slides */
        onPrefetchThreshold?: () => void;

        /** Number of slides from the end when prefetch should be triggered. @default 2 */
        prefetchThreshold?: number;

        /** Number of slides to scroll per click. @default calculated slides in viewport */
        slidesToScroll?: number;

        classNames?: {
            /** @tailwindcss Tailwind classes for the carousel container */
            container?: string;
            /** @tailwindcss Tailwind classes for the carousel button previous */
            buttonPrevious?: string;
            /** @tailwindcss Tailwind classes for the carousel button next */
            buttonNext?: string;
        };
    } & Pick<CarouselContainer.Props, 'children'>
> = props => {
    const spacing = props.spacing ?? 'MD';
    const prefetchThreshold = props.prefetchThreshold ?? 2;

    const containerRef = useRef<HTMLDivElement | null>(null);
    const referenceRef = useRef<HTMLDivElement | null>(null);
    const prefetchTriggered = useRef<number>(-1);

    const numSlidesToShow = useStateObject(1);
    const showPrevButton = useStateObject(false);
    const showNextButton = useStateObject(true);

    const calculateSlidesToShow = useCallback(() => {
        const container = containerRef.current;
        if (!container) return 1;

        const containerWidth = container.offsetWidth;
        const firstSlide = container.querySelector<HTMLElement>(':first-child');
        if (!firstSlide) return 1;

        const slideWidth = firstSlide.offsetWidth;
        return Math.floor(containerWidth / slideWidth);
    }, []);

    const handleScroll = useCallback(
        (direction: 'LEFT' | 'RIGHT') => {
            const container = containerRef.current;
            if (!container) return;

            const firstSlide = container.querySelector<HTMLElement>(':first-child');
            if (!firstSlide) return;

            const actualSlideWidth = firstSlide.offsetWidth;
            const slidesInViewport = numSlidesToShow.value;

            // Use custom slidesToScroll or fallback to slides in viewport
            const requestedSlidesToScroll = props.slidesToScroll ?? slidesInViewport;
            const effectiveSlidesToScroll = Math.min(requestedSlidesToScroll, slidesInViewport);

            const scrollDistance = actualSlideWidth * effectiveSlidesToScroll;
            const { scrollLeft } = container;

            switch (direction) {
                case 'LEFT':
                    container.scrollTo({
                        left: scrollLeft - scrollDistance,
                        behavior: 'smooth',
                    });
                    break;
                case 'RIGHT':
                    container.scrollTo({
                        left: scrollLeft + scrollDistance,
                        behavior: 'smooth',
                    });
                    break;
                default:
                    direction satisfies never;
            }
        },
        [numSlidesToShow, props.slidesToScroll],
    );

    const updateButtonVisibility = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;

        showPrevButton.set(container.scrollLeft > 0);
        showNextButton.set(
            container.scrollLeft + container.clientWidth < container.scrollWidth - 16,
        );
    }, []);

    const handleScrollVisibility = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;

        updateButtonVisibility();

        // Check prefetch when scrolling forward near the end
        if (props.onPrefetchThreshold && container.scrollLeft > 0) {
            const totalSlides = container.children.length;
            const firstSlide = container.querySelector<HTMLElement>(':first-child');
            if (!firstSlide) return;

            const slidesPerPage = numSlidesToShow.value;
            const slideWidth = firstSlide.offsetWidth;
            const currentPage = Math.floor(container.scrollLeft / (slideWidth * slidesPerPage));
            const totalPages = Math.ceil(totalSlides / slidesPerPage);
            const pagesLeft = totalPages - currentPage - 1;

            // Trigger prefetch when near the end, only once per page
            if (pagesLeft <= prefetchThreshold) {
                const lastTriggeredPage = prefetchTriggered.current;
                if (currentPage !== lastTriggeredPage) {
                    prefetchTriggered.current = currentPage;
                    props.onPrefetchThreshold();
                }
            }
        }
    }, [numSlidesToShow, props.onPrefetchThreshold, prefetchThreshold, updateButtonVisibility]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const firstSlide = container.querySelector<HTMLElement>(':first-child');

        const handleResize = () => {
            numSlidesToShow.set(calculateSlidesToShow());

            // Calculate and set margin
            if (firstSlide) {
                const checkIfAllSlidesAreVisible = () => {
                    const allSlidesWidth = Array.from(container.children).reduce(
                        (totalWidth, slide) =>
                            totalWidth + ((slide as HTMLElement).offsetWidth || 0),
                        0,
                    );

                    return allSlidesWidth <= container.offsetWidth;
                };

                // If all slides are visible, center them and remove the outer padding and margin of the first slide
                if (checkIfAllSlidesAreVisible()) {
                    container.classList.remove(OUTER_PADDING_CLASS);
                    if (!props.disableMarginLeft) {
                        container.classList.add(CENTER_CLASS);
                    }
                    showNextButton.set(false);
                    firstSlide.style.marginLeft = '0px';
                    return;
                }

                // Calculate the margin for the first slide, to mimic the start of a container class
                if (referenceRef.current) {
                    const containerRect = referenceRef.current.getBoundingClientRect();
                    const referenceElementPadding = referenceRef.current
                        ? parseFloat(getComputedStyle(referenceRef.current).paddingLeft)
                        : 0;

                    const containerScrollLeft =
                        (containerRect?.left ?? 0) -
                        2 * CarouselContainer.SPACING[spacing] +
                        referenceElementPadding;

                    // eslint-disable-next-line no-param-reassign
                    firstSlide.style.marginLeft = `${
                        props.disableMarginLeft
                            ? 0
                            : containerScrollLeft > 16
                              ? containerScrollLeft
                              : 16
                    }px`;

                    firstSlide.classList.add('snap-end');
                    firstSlide.classList.remove('snap-start');
                }
            }

            // Add some padding to the right of the slider, otherwise the last slide will be glued to the right edge
            container.classList.remove(CENTER_CLASS);
            if (props.spacing !== 'NONE') container.classList.add(OUTER_PADDING_CLASS);

            container.scrollLeft = 0;
        };

        const debouncedResize = debounce(handleResize, 200);

        window.addEventListener('resize', debouncedResize);
        container?.addEventListener('scroll', handleScrollVisibility);

        handleScrollVisibility();
        handleResize();

        return () => {
            container.removeEventListener('scroll', handleScrollVisibility);
            window.removeEventListener('resize', debouncedResize);
        };
    }, [calculateSlidesToShow, spacing, containerRef.current, props.disableMarginLeft]);

    /** Ensure that the next button is visible when a new page is loaded */
    useEffect(() => {
        setTimeout(() => {
            updateButtonVisibility();
        }, 0);
    }, [props.children, updateButtonVisibility]);

    return (
        <div className="relative">
            <div
                ref={referenceRef}
                className="container pointer-events-none z-1"
                style={{ height: 0 }}
            />

            {props.buttons && showPrevButton.value && (
                <motion.div
                    className="pointer-events-none absolute -left-4 top-0 z-1 h-full w-8"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15, ease: 'easeInOut' }}
                />
            )}

            {props.buttons && showNextButton.value && (
                <motion.div
                    className="pointer-events-none absolute -right-4 top-0 z-1 h-full w-8"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15, ease: 'easeInOut' }}
                />
            )}

            <CarouselContainer.Component
                spacing={spacing}
                containerRef={containerRef as RefObject<HTMLDivElement>}
                className={cn(props.classNames?.container)}
            >
                {props.children}
            </CarouselContainer.Component>

            {props.buttons && (
                <>
                    <CarouselButton
                        show={showPrevButton.value}
                        direction="PREVIOUS"
                        onClick={() => handleScroll('LEFT')}
                        className={props.classNames?.buttonPrevious}
                    />
                    <CarouselButton
                        show={showNextButton.value}
                        direction="NEXT"
                        onClick={() => handleScroll('RIGHT')}
                        className={props.classNames?.buttonNext}
                    />
                </>
            )}
        </div>
    );
};

export default Carousel;

const CarouselButton: React.FC<{
    direction: 'PREVIOUS' | 'NEXT';
    onClick?: () => void;
    className?: string;
    /** @default true */
    show: boolean;
}> = props => (
    <motion.button
        initial="hidden"
        animate={props.show ? 'visible' : 'hidden'}
        exit="hidden"
        variants={{
            visible: { opacity: 1, transition: { duration: 0.15, ease: 'easeInOut' } },
            hidden: { opacity: 0, transition: { duration: 0.15, ease: 'easeInOut' } },
        }}
        onClick={props.onClick}
        className={cn(
            'size-10 shadow-lg',
            'top-18 absolute z-1 flex -translate-y-1/2 items-center justify-center rounded-full bg-tertiary text-white transition-colors hover:bg-tertiary-700 md:flex',
            props.direction === 'PREVIOUS' ? 'left-0 -translate-x-1/2' : 'right-0 translate-x-1/2',
            props.className,
        )}
        type="button"
    >
        <Icon name={props.direction === 'PREVIOUS' ? 'arrow-left' : 'arrow-right'} />
    </motion.button>
);

namespace CarouselContainer {
    export type Props = {
        children: React.ReactNode;
        containerRef?: React.RefObject<HTMLDivElement>;
        className?: string;
        spacing: keyof typeof SPACING;
    };

    export const Component: React.FC<Props> = ({
        children,
        spacing = 'MD',
        containerRef,
        className,
    }) => (
        <div ref={containerRef} className={VARIANTS({ spacing, className })}>
            {children}
        </div>
    );

    const VARIANTS = cva(
        [
            'no-scrollbar flex touch-pan-x touch-pan-y snap-x snap-mandatory flex-nowrap overflow-x-auto whitespace-nowrap',
            '*:shrink-0',
            '*:snap-center',
        ],
        {
            variants: {
                spacing: {
                    NONE: '',
                    SM: 'gap-2',
                    MD: 'gap-4',
                    LG: 'gap-8',
                    XL: 'gap-10',
                    '2xl': 'gap-10 md:gap-16',
                } satisfies Record<keyof typeof SPACING, string>,
            },
        },
    );

    export const SPACING = {
        NONE: 0,
        SM: 2,
        MD: 4,
        LG: 8,
        XL: 10,
        '2xl': 16,
    };
}
